import { useBlocNext } from "@blac/react";
import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { ComponentProps, FC } from "react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SignupCustomIneligibility, {
  IneligibleIcon
} from "src/ui/components/SignupCustomContent/SignupCustomIneligibility";
import ClinicalExceptionCubit from "src/ui/components/SignupCustomContent/state/ClinicalExceptionCubit";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import Translate from "src/ui/components/Translate/Translate";

const OfferText = styled.p`
  max-width: 380px;
  margin: 0 auto;
`;

export const ClinicalExceptionOfferText: FC = () => (
  <OfferText className="color-c-80">
    <Translate msg="ineligibility.exceptionOffer.description" />
  </OfferText>
);

const ClinicalExceptionSubmitted: FC = () => {
  const navigate = useNavigate();
  const [, { logout }] = useBlocNext(AuthenticationBloc);

  // An ineligible member has no app access, so /app would bounce them back into
  // the funnel; logging out first lands them on the login screen instead.
  const goToLogin = () => void logout().then(() => navigate("/app"));

  return (
    <SignupCustomIneligibility icon={IneligibleIcon.FORM_EDIT}>
      <nine-spacer s="md"></nine-spacer>
      <h4>
        <Translate msg="ineligibility.exceptionSubmitted.title" />
      </h4>
      <nine-spacer s="md"></nine-spacer>
      <OfferText className="color-c-80">
        <Translate msg="ineligibility.exceptionSubmitted.description" />
      </OfferText>
      <nine-spacer s="md"></nine-spacer>
      <Button onPress={goToLogin} theme="charcoal" center>
        <Translate msg="ineligibility.exceptionSubmitted.button" />
      </Button>
    </SignupCustomIneligibility>
  );
};

/** Ineligibility screen that switches to a submitted confirmation once the member has an active exception. */
export const ClinicalExceptionIneligibility: FC<
  Omit<ComponentProps<typeof SignupCustomIneligibility>, "primaryAction">
> = (props) => {
  const [
    { requested, submitting },
    { loadRequestedException, requestException }
  ] = useBlocNext(ClinicalExceptionCubit);

  useEffect(() => {
    void loadRequestedException();
  }, [loadRequestedException]);

  if (requested) {
    return <ClinicalExceptionSubmitted />;
  }

  return (
    <SignupCustomIneligibility
      {...props}
      primaryAction={{
        label: <Translate msg="ineligibility.exceptionOffer.button" />,
        onPress: () => void requestException(),
        isDisabled: submitting
      }}
    />
  );
};
